import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, RefreshCw, Copy, Check, AlertCircle, Key, Activity, TrendingUp } from 'lucide-react';
import apiKeyService, {
  ApiKey,
  ApiScopes,
  CreateApiKeyInput,
  UpdateApiKeyInput,
  copyToClipboard,
  formatRelativeTime,
  formatNumber,
  getScopeCategory,
  getScopeCategoryColor,
} from '../../services/apiKeyService';
import ApiKeyLogs from './ApiKeyLogs';

const ApiKeys: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [scopes, setScopes] = useState<ApiScopes | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedKeyForStats, setSelectedKeyForStats] = useState<string | null>(null);

  useEffect(() => {
    loadApiKeys();
    loadScopes();
  }, []);

  const loadApiKeys = async () => {
    try {
      const data = await apiKeyService.getApiKeys();
      setApiKeys(data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScopes = async () => {
    try {
      const data = await apiKeyService.getScopes();
      setScopes(data);
    } catch (error) {
      console.error('Failed to load scopes:', error);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleDelete = async (keyId: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiKeyService.deleteApiKey(keyId);
      await loadApiKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
      alert('Failed to delete API key');
    }
  };

  const handleRevoke = async (keyId: string, keyName: string) => {
    if (!confirm(`Revoke "${keyName}"? This will immediately disable all API requests using this key.`)) {
      return;
    }

    try {
      await apiKeyService.revokeApiKey(keyId);
      await loadApiKeys();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      alert('Failed to revoke API key');
    }
  };

  const handleRotateSecret = async (keyId: string, keyName: string) => {
    if (!confirm(`Rotate secret for "${keyName}"? The old secret will immediately stop working.`)) {
      return;
    }

    try {
      const result = await apiKeyService.rotateSecret(keyId);
      alert(`New Secret: ${result.secret}\n\nStore this securely! It will not be shown again.`);
      await loadApiKeys();
    } catch (error) {
      console.error('Failed to rotate secret:', error);
      alert('Failed to rotate secret');
    }
  };

  const handleEdit = (apiKey: ApiKey) => {
    setEditingKey(apiKey);
    setShowEditModal(true);
  };

  const handleViewStats = (keyId: string) => {
    setSelectedKeyForStats(keyId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (selectedKeyForStats) {
    return (
      <ApiKeyStats
        apiKeyId={selectedKeyForStats}
        onBack={() => setSelectedKeyForStats(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">API Keys</h1>
          <p className="mt-2 text-gray-600">
            Manage API keys for programmatic access to your account
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
        >
          <Plus size={20} />
          New API Key
        </button>
      </div>

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Key size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No API Keys</h3>
          <p className="text-gray-600 mb-4">
            Create an API key to start accessing the API programmatically
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Create Your First API Key
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {apiKeys.map((apiKey) => (
            <div key={apiKey.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-semibold text-gray-900">{apiKey.name}</h3>
                    {apiKey.isActive ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                  {apiKey.expiresAt && (
                    <p className="text-sm text-gray-600">
                      Expires {formatRelativeTime(apiKey.expiresAt)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleViewStats(apiKey.id)}
                    className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                    title="View Statistics"
                  >
                    <Activity size={18} />
                  </button>
                  <button
                    onClick={() => handleEdit(apiKey)}
                    className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(apiKey.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* API Key */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-50 rounded border text-sm font-mono">
                      {apiKey.key}
                    </code>
                    <button
                      onClick={() => handleCopy(apiKey.key, `key-${apiKey.id}`)}
                      className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                      title="Copy"
                    >
                      {copiedField === `key-${apiKey.id}` ? (
                        <Check size={18} className="text-green-600" />
                      ) : (
                        <Copy size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      API Secret
                    </label>
                    <button
                      onClick={() => handleRotateSecret(apiKey.id, apiKey.name)}
                      className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                    >
                      <RefreshCw size={12} />
                      Rotate
                    </button>
                  </div>
                  <div className="px-3 py-2 bg-gray-50 rounded border text-sm text-gray-500 font-mono">
                    ••••••••••••••••
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Secret is only shown once after creation or rotation
                  </p>
                </div>
              </div>

              {/* Scopes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scopes ({apiKey.scopes.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {apiKey.scopes.map((scope) => {
                    const category = getScopeCategory(scope);
                    const colorClass = getScopeCategoryColor(category);
                    return (
                      <span
                        key={scope}
                        className={`px-2 py-1 text-xs rounded-full font-mono ${colorClass}`}
                      >
                        {scope}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Stats */}
              <div className="mt-4 pt-4 border-t grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Rate Limit</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatNumber(apiKey.rateLimit)}/hr
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Requests</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatNumber(apiKey.requestCount)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Last Used</div>
                  <div className="text-sm font-medium text-gray-900">
                    {apiKey.lastUsedAt ? formatRelativeTime(apiKey.lastUsedAt) : 'Never'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Created</div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatRelativeTime(apiKey.createdAt)}
                  </div>
                </div>
              </div>

              {/* Actions */}
              {apiKey.isActive && (
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => handleRevoke(apiKey.id, apiKey.name)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Revoke API Key
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && scopes && (
        <CreateApiKeyModal
          scopes={scopes}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadApiKeys();
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingKey && scopes && (
        <EditApiKeyModal
          apiKey={editingKey}
          scopes={scopes}
          onClose={() => {
            setShowEditModal(false);
            setEditingKey(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingKey(null);
            loadApiKeys();
          }}
        />
      )}
    </div>
  );
};

// Create API Key Modal Component
const CreateApiKeyModal: React.FC<{
  scopes: ApiScopes;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ scopes, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateApiKeyInput>({
    name: '',
    scopes: [],
    rateLimit: 1000,
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [newApiKey, setNewApiKey] = useState<ApiKey | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (formData.scopes.length === 0) {
        setError('At least one scope is required');
        setLoading(false);
        return;
      }

      const apiKey = await apiKeyService.createApiKey(formData);
      setNewApiKey(apiKey);
      setShowSecret(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create API key');
      setLoading(false);
    }
  };

  const handleScopeToggle = (scopeValue: string) => {
    const newScopes = formData.scopes.includes(scopeValue)
      ? formData.scopes.filter((s) => s !== scopeValue)
      : [...formData.scopes, scopeValue];
    setFormData({
      ...formData,
      scopes: newScopes,
    });
  };

  if (showSecret && newApiKey) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <Check size={24} />
              <h2 className="text-2xl font-bold">API Key Created Successfully!</h2>
            </div>
            <p className="text-gray-600">
              Save these credentials securely. The secret will not be shown again.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key
              </label>
              <code className="block px-3 py-2 bg-gray-50 rounded border text-sm font-mono">
                {newApiKey.key}
              </code>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Secret
              </label>
              <code className="block px-3 py-2 bg-yellow-50 border border-yellow-300 rounded text-sm font-mono">
                {newApiKey.secret}
              </code>
              <div className="flex items-start gap-2 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Copy this secret now. You won't be able to see it again.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                setShowSecret(false);
                onSuccess();
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Create API Key</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="My API Key"
                required
              />
            </div>

            {/* Rate Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate Limit (requests per hour)
              </label>
              <input
                type="number"
                value={formData.rateLimit}
                onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                min="1"
                max="10000"
              />
            </div>

            {/* Scopes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scopes * (Select at least one)
              </label>
              <div className="space-y-4">
                {/* Read Scopes */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Read Access</h4>
                  {scopes.read.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={formData.scopes.includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">{scope.label}</div>
                        <div className="text-xs text-gray-600">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Write Scopes */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Write Access</h4>
                  {scopes.write.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={formData.scopes.includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">{scope.label}</div>
                        <div className="text-xs text-gray-600">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Admin Scopes */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Admin Access</h4>
                  {scopes.admin.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={formData.scopes.includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">{scope.label}</div>
                        <div className="text-xs text-gray-600">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create API Key'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit API Key Modal (simplified version for name, scopes, rate limit, active status)
const EditApiKeyModal: React.FC<{
  apiKey: ApiKey;
  scopes: ApiScopes;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ apiKey, scopes, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<UpdateApiKeyInput>({
    name: apiKey.name,
    scopes: apiKey.scopes,
    rateLimit: apiKey.rateLimit,
    isActive: apiKey.isActive,
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await apiKeyService.updateApiKey(apiKey.id, formData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update API key');
      setLoading(false);
    }
  };

  const handleScopeToggle = (scopeValue: string) => {
    const currentScopes = formData.scopes || [];
    const newScopes = currentScopes.includes(scopeValue)
      ? currentScopes.filter((s) => s !== scopeValue)
      : [...currentScopes, scopeValue];
    setFormData({
      ...formData,
      scopes: newScopes,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Edit API Key</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Rate Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate Limit (requests per hour)
              </label>
              <input
                type="number"
                value={formData.rateLimit || 1000}
                onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                min="1"
                max="10000"
              />
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Inactive API keys cannot be used for API requests
              </p>
            </div>

            {/* Scopes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scopes
              </label>
              <div className="space-y-4">
                {/* Read */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Read Access</h4>
                  {scopes.read.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={(formData.scopes || []).includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">{scope.label}</div>
                        <div className="text-xs text-gray-600">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Write */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Write Access</h4>
                  {scopes.write.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={(formData.scopes || []).includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">{scope.label}</div>
                        <div className="text-xs text-gray-600">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Admin */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Admin Access</h4>
                  {scopes.admin.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={(formData.scopes || []).includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">{scope.label}</div>
                        <div className="text-xs text-gray-600">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update API Key'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// API Key Stats Component (separate view)
const ApiKeyStats: React.FC<{
  apiKeyId: string;
  onBack: () => void;
}> = ({ apiKeyId, onBack }) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'logs'>('overview');

  useEffect(() => {
    loadStats();
  }, [apiKeyId]);

  const loadStats = async () => {
    try {
      const data = await apiKeyService.getUsageStats(apiKeyId);
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="text-indigo-600 hover:text-indigo-700 flex items-center gap-2"
      >
        ← Back to API Keys
      </button>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">API Key Usage</h2>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <button
            className={`pb-2 border-b-2 text-sm font-medium ${
              activeTab === 'overview'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`pb-2 border-b-2 text-sm font-medium ${
              activeTab === 'logs'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('logs')}
          >
            Logs
          </button>
        </nav>
      </div>

      {activeTab === 'overview' ? (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <TrendingUp size={20} />
                <span className="text-sm">Total Requests</span>
              </div>
              <div className="text-3xl font-bold text-gray-900">{formatNumber(stats.totalRequests)}</div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600 mb-2">Last 24 Hours</div>
              <div className="text-3xl font-bold text-gray-900">{formatNumber(stats.last24Hours)}</div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600 mb-2">Last 7 Days</div>
              <div className="text-3xl font-bold text-gray-900">{formatNumber(stats.last7Days)}</div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600 mb-2">Avg Response Time</div>
              <div className="text-3xl font-bold text-gray-900">{stats.averageResponseTime}ms</div>
            </div>
          </div>

          {/* Top Endpoints */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Endpoints</h3>
            <div className="space-y-2">
              {stats.topEndpoints.map((endpoint: any, index: number) => (
                <div key={index} className="flex justify-between items-center">
                  <code className="text-sm font-mono text-gray-700">{endpoint.endpoint}</code>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(endpoint.count)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status Codes */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Codes</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {Object.entries(stats.statusCodes).map(([code, count]) => (
                <div key={code} className="flex justify-between items-center">
                  <span className="text-sm font-mono text-gray-700">{code}</span>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(count as number)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <ApiKeyLogs apiKeyId={apiKeyId} />
      )}
    </div>
  );
};

export default ApiKeys;
